import { ProjectStatus as PS } from '../../schema/types.d'

const MS_PER_DAY = 24 * 3600 * 1000

function isStringArray(x: any): x is string[] {
  return Array.isArray(x) && x.every(el => typeof el === 'string')
}

function isStringArrayArray(x: any): x is string[][] {
  return Array.isArray(x) && x.every(el => isStringArray(el))
}

class Period {
  start: Date
  end: Date
  tag: number[] | string

  constructor(startDate: string, endDate: string, tag: number[] | string) {
    this.start = new Date(startDate)
    this.end = new Date(endDate)
    this.tag = tag
  }

  days() {
    return (
      1 + Math.floor((this.end.valueOf() - this.start.valueOf()) / MS_PER_DAY)
    )
  }

  distribution(): number[][] | string[] {
    const nSlots = this.days()
    if (typeof this.tag === 'string') {
      return Array(nSlots).fill(this.tag)
    } else {
      const perSlot = this.tag.map(q => q / nSlots)
      return Array(nSlots).fill(perSlot)
    }
  }

  print(label = '') {
    const startDate = this.start.toLocaleDateString()
    const endDate = this.end.toLocaleDateString()
    console.log(`${label}: ${startDate} to ${endDate} - ${this.days()} days`)
    console.log(`distribution: ${this.distribution()}`)
  }
}

class Timeline {
  start: Date
  end: Date
  slots: number[][] | string[][]
  step: number

  constructor(startDate: Date, endDate: Date, step: number) {
    this.start = startDate
    this.end = endDate
    this.slots = Array(this.days()).fill([])
    this.step = step
  }

  addPeriod(p: Period) {
    const diff = Math.floor(
      (p.start.valueOf() - this.start.valueOf()) / MS_PER_DAY
    )
    const pDays = p.days()
    const pDist = p.distribution()
    for (let i = 0; i < pDays; i++) {
      // merge
      if (isStringArray(pDist)) {
        // const dist = <string[]>this.slots[i + diff]
        // dist.push(pDist[i])
        this.slots[i + diff] = [pDist[i]]
      } else {
        const dist = <number[]>this.slots[i + diff]
        dist.map((e, idx) => (pDist[i][idx] += e))
        this.slots[i + diff] = pDist[i]
      }
    }
  }

  fold() {
    const nSlots = Math.floor(this.days() / this.step)
    const folded_slots = new Array(nSlots).fill([])
    for (let i = 0; i < nSlots; i++) {
      const slice = this.slots.slice(i * this.step, (i + 1) * this.step)

      if (isStringArrayArray(slice)) {
        folded_slots[i] = slice.reduce((z, a) => [...new Set(z.concat(a))])
      } else {
        folded_slots[i] = slice.reduce((z, a) =>
          z.length > 0 ? (a.length > 0 ? z.map((e, idx) => e + a[idx]) : z) : a
        )
      }
    }
    this.slots = folded_slots
  }

  days() {
    return (
      1 + Math.floor((this.end.valueOf() - this.start.valueOf()) / MS_PER_DAY)
    )
  }

  print(label = '') {
    const startDate = this.start.toLocaleDateString()
    const endDate = this.end.toLocaleDateString()
    console.log(
      `${label}: ${startDate} to ${endDate} - ${this.days() / this.step} slots`
    )
    if (isStringArrayArray(this.slots)) {
    } else {
      console.log(
        `slots: ${this.slots.map(a => (a.length > 0 ? '[' + a + ']' : '.'))}`
      )
    }
  }
}

export class ProjectStatus {
  budget: PS.Budget
  plan: PS.Plan
  labor: PS.Labor[]
  start: Date
  end: Date
  step: number

  constructor(project_data: any, step = 1) {
    this.budget = project_data.budget
    this.plan = project_data.plan
    this.labor = project_data.labor
    this.findDateRange()
    this.step = step
  }

  findDateRange() {
    const dates: Array<Date> = []
    if (this.budget && this.budget.resources)
      this.budget.resources.map((r: any) => {
        dates.push(new Date(r.startDate))
        dates.push(new Date(r.endDate))
      })
    if (this.plan && this.plan.resources)
      this.plan.resources.map((r: any) => {
        dates.push(new Date(r.startDate))
        dates.push(new Date(r.endDate))
      })
    if (this.labor)
      this.labor.map((r: any) => {
        dates.push(new Date(r.laborDate))
      })
    if (dates.length > 0) {
      const minDate = Math.min.apply(null, dates)
      const maxDate = Math.max.apply(null, dates)
      this.start = new Date(minDate)
      this.end = new Date(maxDate)
    } else {
      this.start = null
      this.end = null
    }
  }

  resetDates(start: Date, end: Date) {
    this.start = new Date(start)
    this.end = new Date(end)
  }

  dateHeaders() {
    const date_headers = []
    const tmp_date = new Date(this.start)
    while (tmp_date <= this.end) {
      date_headers.push(tmp_date.toLocaleDateString())
      tmp_date.setDate(tmp_date.getDate() + this.step)
    }
    return date_headers
  }

  timelines(records: any, fn: (_: any) => Period) {
    const periodsByResId: Map<string, Array<Period>> = new Map()
    records.map((l: any) => {
      const ps = periodsByResId.get(l.resource.id) || []
      const p = fn(l)
      if (p.start >= this.start && p.end <= this.end) ps.push(p)
      periodsByResId.set(l.resource.id, ps)
    })
    const timelinesByResId: Map<string, number[][]> = new Map()
    periodsByResId.forEach((ps, r, _) => {
      const t = new Timeline(this.start, this.end, this.step)
      ps.map(p => t.addPeriod(p))
      t.fold()
      timelinesByResId.set(r, <number[][]>t.slots)
    })
    return timelinesByResId
  }

  budget_timeline_by_resources() {
    const p = (l: any) =>
      new Period(l.startDate, l.endDate, [l.laborHours, l.laborCost])
    return this.budget ? this.timelines(this.budget.resources, p) : null
  }

  plan_timeline_by_resources() {
    const p = (l: any) =>
      new Period(l.startDate, l.endDate, [l.laborHours, l.laborCost])
    return this.plan ? this.timelines(this.plan.resources, p) : null
  }

  labor_timeline_by_resources() {
    const p = (l: any) =>
      new Period(l.laborDate, l.laborDate, [
        l.regularHours,
        l.overtimeHours,
        l.specialOvertimeHours
      ])
    return this.labor ? this.timelines(this.labor, p) : null
  }

  phase_timeline() {
    const phasesById: Map<string, [string, string, string]> = new Map()
    const updatePhases = (r: {
      startDate: string
      endDate: string
      phase: PS.Phase
    }) => {
      const phase = phasesById.get(r.phase.id) || [
        r.startDate,
        r.endDate,
        r.phase.name
      ]
      console.log('phase name ', r.phase.name)

      if (new Date(r.startDate) < new Date(phase[0])) phase[0] = r.startDate
      if (new Date(r.endDate) > new Date(phase[1])) phase[1] = r.startDate
      phasesById.set(r.phase.id, phase)
    }
    this.budget && this.budget.resources.map(updatePhases)
    this.plan && this.plan.resources.map(updatePhases)
    // this.labor.map(l =>
    //   updatePhases({
    //     startDate: l.laborDate,
    //     endDate: l.laborDate,
    //     phase: l.phase
    //   })
    // )
    if (this.start && this.end) {
      const timeline = new Timeline(this.start, this.end, this.step)
      phasesById.forEach(([st, end, name], id) => {
        if (new Date(st) >= this.start && new Date(end) <= this.end)
          timeline.addPeriod(new Period(st, end, name + id))
      })
      timeline.fold()
      return <string[][]>timeline.slots
    } else return []
  }
}

function test() {
  const json = require('./sample_data.json')
  const status = new ProjectStatus(json.data.project, 30)
  const lt = status.labor_timeline_by_resources()
  const bt = status.budget_timeline_by_resources()
  const pt = status.plan_timeline_by_resources()

  var timeline = lt.get('50157')
  const print_timeline = (slots: number[][]) =>
    console.log(
      `slots: ${slots.map(a => (a.length > 0 ? '[' + a + ']' : '.'))}`
    )

  // timeline && print_timeline(timeline)
  timeline = bt.get('50157')
  console.log(bt.keys())
  timeline && print_timeline(timeline)
  timeline = pt.get('50157')
  timeline && print_timeline(timeline)
}

// test()
